import { Directive, ElementRef, OnDestroy } from '@angular/core';

import { EventModel, safeEventId } from '@frontend/sports/betting-offer/feature/model';
import { MutationObserverService } from '@frontend/sports/common/core/utils/dom';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { Subject, takeUntil } from 'rxjs';

import { TreeItemType } from '../event-details-common.models';

interface IReferenceElement {
    getOffset(): number;
}

class LeagueReferenceElement implements IReferenceElement {
    constructor(public leagueElm: HTMLElement) {}

    getOffset(): number {
        return this.leagueElm.offsetTop;
    }
}

class EventReferenceEvent implements IReferenceElement {
    constructor(
        private eventElm: HTMLElement,
        private leagueElm: HTMLElement,
        private eventsWrapperElm: HTMLElement | undefined,
        private distance: number = 0,
    ) {}

    getOffset(): number {
        const eventsWrapperHeight = this.eventsWrapperElm ? this.eventsWrapperElm.clientHeight : 0;
        const leagueOffset = this.leagueElm?.offsetTop ?? 0;
        const leagueClientHeight = this.leagueElm?.clientHeight ?? 0;

        return leagueOffset + leagueClientHeight - eventsWrapperHeight + this.eventElm.offsetTop - this.distance;
    }
}

@Directive({
    selector: '[msTreeScroll]',
    standalone: true,
})
export class TreeScrollDirective implements OnDestroy {
    private eventToScroll: EventModel | undefined;
    private offset: number;
    private referenceElement: IReferenceElement | undefined;
    private unsubscribe$ = new Subject<void>();

    constructor(
        public elementRef: ElementRef,
        public scrollContainer: ScrollContainerService,
        private mutationObserver: MutationObserverService,
    ) {}

    ngOnDestroy(): void {
        this.stopAdjusting();
        this.unsubscribe$.complete();
    }

    preserveScrolling(func: () => Promise<void>, event?: EventModel): void {
        this.eventToScroll = event;

        if (!this.eventToScroll) {
            this.referenceElement = this.findTopElement();
        }

        if (this.referenceElement) {
            this.offset = this.referenceElement.getOffset();
        }

        if (this.referenceElement || this.eventToScroll) {
            this.startAdjusting();
            func().then(() => {
                this.stopAdjusting();
            });
        } else {
            func();
        }
    }

    scrollToCenterOrTop(event: EventModel): void {
        const eventElm = this.findEventElement(event, this.getCenterPoint());
        if (eventElm) {
            const newOffset = eventElm.getOffset();
            this.scrollContainer.scrollTo(undefined, -newOffset);
        } else if (this.scrollContainer.scrollTop !== 0) {
            this.scrollContainer.scrollTo(undefined, 0);
        }
    }

    scrollTo(event: EventModel): void {
        const eventElm = this.findEventElement(event);
        if (eventElm) {
            const newOffset = eventElm.getOffset();
            this.scrollContainer.scrollTo(undefined, -newOffset);
        }
    }

    private getCenterPoint(): number | undefined {
        const target = this.scrollContainer.target;

        if (target instanceof HTMLElement) {
            return target.offsetHeight / 2;
        }
        if (target instanceof Window) {
            return target.innerHeight / 2;
        }

        return;
    }

    private findEventElement(event: EventModel, distance: number = 150): IReferenceElement | undefined {
        const eventElm = this.elementRef.nativeElement.querySelector(`#event-${safeEventId(event.id)}`);
        if (!eventElm) {
            return undefined;
        }

        const leagueId = event.league.realCompetitionId || event.league.parentLeagueId || event.league.id;

        const favouriteLeagueElm = this.elementRef.nativeElement.querySelector(`#item-${TreeItemType.FavouriteLeague}-${leagueId}`);
        if (favouriteLeagueElm) {
            return new EventReferenceEvent(eventElm, favouriteLeagueElm, undefined, distance);
        }

        const leagueElm = this.elementRef.nativeElement.querySelector(`#item-${TreeItemType.League}-${leagueId}`);

        return new EventReferenceEvent(eventElm, leagueElm, undefined, distance);
    }

    private findTopElement(): IReferenceElement | undefined {
        let result: IReferenceElement | undefined;
        const sports: any[] = this.elementRef.nativeElement.querySelectorAll('.sport');
        if (!sports) {
            return undefined;
        }

        for (const sport of sports) {
            if (this.isOnTop(sport)) {
                const leagues = sport.querySelectorAll('.league');

                for (const league of leagues) {
                    if (league && this.isOnTop(league)) {
                        const eventsWrapper = league.querySelector('.tree-events');
                        const eventsWrapperHeight = eventsWrapper ? eventsWrapper.clientHeight : 0;
                        const leagueOffset = league.offsetTop + league.clientHeight - eventsWrapperHeight;

                        const events = league.querySelectorAll('ms-tree-event');
                        events.forEach((event: any) => {
                            if (this.isEventOnTop(event, leagueOffset)) {
                                result = new EventReferenceEvent(event, league, eventsWrapper);
                            }
                        });

                        if (!result) {
                            result = new LeagueReferenceElement(league);
                        }

                        return result;
                    }
                }
            }
        }

        return undefined;
    }

    private startAdjusting(): void {
        this.mutationObserver
            .observe(this.elementRef.nativeElement)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                if (!this.referenceElement && this.eventToScroll) {
                    this.referenceElement = this.findEventElement(this.eventToScroll);
                }

                if (this.referenceElement) {
                    this.adjustScrollPosition(this.referenceElement);
                }
            });
    }

    private adjustScrollPosition(elm: IReferenceElement): void {
        const newOffset = elm.getOffset();
        if (newOffset === 0 || newOffset === this.offset) {
            return;
        }

        let scrollTo: number;
        if (this.offset) {
            scrollTo = this.scrollContainer.scrollTop + newOffset - this.offset;
        } else {
            scrollTo = newOffset;
        }

        this.offset = newOffset;
        this.scrollContainer.scrollTo(undefined, -scrollTo);
    }

    private isEventOnTop(event: HTMLElement, leagueOffset: number): boolean {
        const eventOffset = event.offsetTop + leagueOffset;

        return event && eventOffset < this.scrollContainer.scrollTop && eventOffset + event.clientHeight > this.scrollContainer.scrollTop;
    }

    private isOnTop(item: HTMLElement): boolean {
        return item && item.offsetTop < this.scrollContainer.scrollTop && item.offsetTop + item.clientHeight > this.scrollContainer.scrollTop;
    }

    private stopAdjusting(): void {
        this.unsubscribe$.next();

        this.referenceElement = undefined;
        this.eventToScroll = undefined;
    }
}
